import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X } from "lucide-react";
import { MERCH } from "./catalog";
import WaitlistForm from "./WaitlistForm";

export default function Shop() {
  const [modal, setModal] = useState(null); // product name or null

  useEffect(() => {
    document.body.style.overflow = modal ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [modal]);

  return (
    <section
      id="shop"
      data-testid="shop-section"
      className="py-16 md:py-36 bg-black border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-12 md:mb-14"
        >
          <p className="font-mono-accent text-xs tracking-[0.35em] uppercase text-white/40 mb-6">
            Gear Up
          </p>
          <h2 className="font-display font-black uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl md:text-7xl">
            Shop Speed Factory
          </h2>
          <p className="mt-6 text-white/60 text-base md:text-lg">
            Official JJ Speed Factory apparel. First drop launching soon — get
            notified the moment it's live.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {MERCH.map((m, i) => (
            <motion.div
              key={m.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 3) * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`merch-card-${i}`}
              className="group flex flex-col bg-[#0a0a0a] border border-white/10 hover:border-white/30 transition-all duration-500 hover:-translate-y-1"
            >
              <div className="relative aspect-square overflow-hidden border-b border-white/10 bg-[#111]">
                <img src={m.img} alt={m.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <span className="absolute top-3 right-3 font-mono-accent text-[9px] tracking-[0.18em] uppercase text-white/70 border border-white/25 px-2 py-1 bg-black/50 backdrop-blur-sm">
                  {m.price}
                </span>
              </div>
              <div className="flex flex-col flex-1 p-6">
                <h3 className="font-display font-bold uppercase tracking-tight text-xl leading-none mb-2">{m.name}</h3>
                <p className="text-white/50 text-sm flex-1">{m.desc}</p>
                <button
                  data-testid={`merch-notify-${i}`}
                  onClick={() => setModal(m.name)}
                  className="mt-6 inline-flex items-center justify-center gap-2 border border-white/20 text-white uppercase tracking-[0.12em] font-bold text-[11px] py-3 hover:bg-white hover:text-black transition-colors duration-300"
                >
                  <Bell size={14} /> Notify Me
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {modal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setModal(null)}
            data-testid="merch-notify-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 20 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg bg-[#0b0b0b] border border-white/10 p-6 md:p-10 max-h-[90vh] overflow-y-auto"
            >
              <button
                data-testid="merch-modal-close"
                onClick={() => setModal(null)}
                className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
              >
                <X size={22} />
              </button>
              <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/40 mb-2">Get Notified</p>
              <h3 className="font-display font-black uppercase tracking-tight text-2xl md:text-3xl mb-6">{modal}</h3>
              <WaitlistForm source={`Merch: ${modal}`} onSuccess={() => setTimeout(() => setModal(null), 1600)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
